import _ from "lodash";
import { DATATEST } from "constants/integration";
import { AccordionSummary, makeStyles } from "@material-ui/core";
import React, { FC, useCallback, useMemo } from "react";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import {
    NotificationInfo,
    NotificationError,
    NotificationLevel,
    NotificationWarning,
    NotificationTemplate,
} from "components/notification-banners";
import { postToWindow } from "helpers/window-messages";
import { autoReduce, dismissNotification, removeNotifications, reviewManually } from "actions/cart-legacy";
import { format } from "lib/globalize-util";
import Grid from "@material-ui/core/Grid";
import theme from "styles/theme";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { INotification } from "reducers/cart-notifications";

interface IProps {
    notifications: INotification[];
}

const CUSTOM_NOTIFICATION_TYPES = {
    OVER_ORDERED: "exceedQuantities",
};

export const Notifications: FC<IProps> = ({ notifications = [] }) => {
    const classes = useStyles();

    const handleClose = useCallback(
        (notification: INotification) => () => postToWindow(dismissNotification(notification)),
        []
    );

    const [customNotifications, staticNotifications] = useMemo(
        () => _.partition(notifications, ({ id }) => _.includes(_.values(CUSTOM_NOTIFICATION_TYPES), id)),
        [notifications]
    );

    const { info, success, error, warning } = useMemo(() => _.groupBy(notifications, "type"), [notifications]);

    return (
        <Accordion classes={{ root: classes.accordionRoot }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                classes={{ root: classes.summaryRoot }}
                data-test={DATATEST.CART_NOTIFICATIONS.ALERTS.ROOT}
            >
                <Grid container={true} className={classes.alertsContainer}>
                    <Grid item={true} className={classes.alertsLabel}>
                        {format("Alerts")}
                    </Grid>
                    <Grid item={true}>
                        {!_.isEmpty(error) && (
                            <span data-test={DATATEST.CART_NOTIFICATIONS.ALERTS.ERROR} className={classes.alertError}>
                                {error.length}
                            </span>
                        )}
                        {!_.isEmpty(info) && (
                            <span data-test={DATATEST.CART_NOTIFICATIONS.ALERTS.INFO} className={classes.alertInfo}>
                                {info.length}
                            </span>
                        )}
                        {!_.isEmpty(warning) && (
                            <span
                                data-test={DATATEST.CART_NOTIFICATIONS.ALERTS.WARNING}
                                className={classes.alertWarning}
                            >
                                {warning.length}
                            </span>
                        )}
                        {!_.isEmpty(success) && (
                            <span
                                data-test={DATATEST.CART_NOTIFICATIONS.ALERTS.SUCCESS}
                                className={classes.alertSuccess}
                            >
                                {success.length}
                            </span>
                        )}
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails classes={{ root: classes.detailsWrapper }}>
                {_.map(staticNotifications, (notification) => {
                    switch (notification.type) {
                        case NotificationLevel.Warning:
                            return (
                                <NotificationWarning
                                    classes={{ root: classes.notificationRoot }}
                                    message={notification.message}
                                    onClose={notification.dismiss ? handleClose(notification) : null}
                                />
                            );
                        case NotificationLevel.Info:
                            return (
                                <NotificationInfo
                                    classes={{ root: classes.notificationRoot }}
                                    message={notification.message}
                                    onClose={notification.dismiss ? handleClose(notification) : null}
                                />
                            );
                        case NotificationLevel.Error:
                            return (
                                <NotificationError
                                    classes={{ root: classes.notificationRoot }}
                                    message={notification.message}
                                    onClose={notification.dismiss ? handleClose(notification) : null}
                                />
                            );
                        default:
                            return null;
                    }
                })}
                {_.map(customNotifications, (notification) => {
                    switch (notification.id) {
                        case CUSTOM_NOTIFICATION_TYPES.OVER_ORDERED:
                            return <OverOrderingNotification notification={notification} />;
                        default:
                            return null;
                    }
                })}
            </AccordionDetails>
        </Accordion>
    );
};

const OverOrderingNotification: FC<{ notification: INotification }> = ({ notification }) => {
    const classes = useStyles();
    const overOrderingClasses = useOverOrderingStyles();

    const handleClose = useCallback(
        (notification: INotification) => () => postToWindow(removeNotifications(notification)),
        []
    );

    const handleAutoReduce = useCallback(() => postToWindow(autoReduce), []);
    const handleReviewManually = useCallback(() => postToWindow(reviewManually), []);

    return (
        <NotificationTemplate
            dataTest={DATATEST.NOTIFICATION_BANNER.ORDERING}
            classes={{ root: classes.notificationRoot }}
            message={notification.message}
            level={NotificationLevel.Error}
            onClose={notification.dismiss ? handleClose(notification) : null}
        >
            <Grid container={true} className={overOrderingClasses.actionsContainer}>
                <button className={overOrderingClasses.button} onClick={handleReviewManually}>
                    {format("Review Manually")}
                </button>
                <button className={overOrderingClasses.button} onClick={handleAutoReduce}>
                    {format("Auto Reduce")}
                </button>
            </Grid>
        </NotificationTemplate>
    );
};

const useStyles = makeStyles({
    accordionRoot: {
        margin: "0 0 15px",
        "& .MuiAccordionSummary-root.Mui-expanded": {
            height: 32,
            minHeight: "auto",
        },
    },
    summaryRoot: {
        height: 32,
        minHeight: "auto",
        flexDirection: "row-reverse",
        padding: 0,
    },
    alertsContainer: {
        "& span": {
            borderRadius: 9,
            color: theme.palette.white,
            fontSize: 12,
            padding: "0 10px",
            margin: "0 5px",
        },
    },
    alertError: {
        background: theme.palette.errorColor,
    },
    alertInfo: {
        background: theme.palette.infoAlertTextColor,
    },
    alertWarning: {
        background: theme.palette.warningAlertIconSignColor,
    },
    alertSuccess: {
        background: theme.palette.successAlertTextColor,
    },
    alertsLabel: {
        color: theme.palette.darkerGrey,
        fontSize: 14,
        marginRight: 3,
    },
    detailsWrapper: {
        flexDirection: "column",
    },
    notificationRoot: {
        margin: "9px 0",
    },
});

const useOverOrderingStyles = makeStyles({
    actionsContainer: {
        minWidth: 260,
    },
    button: {
        borderRadius: 24,
        height: 28,
        textTransform: "capitalize",
        color: "#2d2d2d",
        margin: "0 5px",
        padding: "0 15px",
        backgroundColor: "transparent",
        border: "1px solid #2d2d2d",
    },
});
